import { getStyle as getFeatureStyle, getClusterStyle } from '../feature/styles';
import { CUSTOM_STYLING_RULE_OTHER, NEDGLOBE_VIEWER, STYLE_TYPE, STYLE_CLASSIFICATION_TYPES } from '../../../constants';

export function getStyle(computedStyles, feature, resolution, displayType = NEDGLOBE_VIEWER.styleDisplayTypes.default, feedbackFields = null) {
    const { zIndex } = STYLE_TYPE[displayType];
    const hideFill = computedStyles[0].hideFill;
    let currentStyle = computedStyles[0].settings[displayType.toLowerCase()];
    currentStyle.hideFill = hideFill;
    if (displayType === NEDGLOBE_VIEWER.styleDisplayTypes.default) {
        const customStylingOptions = computedStyles[0].customStylingOptions;
        if (customStylingOptions) {
            currentStyle = getCustomStyle(feature.getProperties(), customStylingOptions, hideFill);

            // Add labels to currentstyle in case of advanced styling
            currentStyle.labels = computedStyles[0].settings?.default?.labels;
            currentStyle.textMaxResolution = computedStyles[0].textMaxResolution;
            currentStyle.textFillColor = computedStyles[0].settings.default.textFillColor;
            currentStyle.textFont = computedStyles[0].settings.default.textFont;
            currentStyle.textFontSize = computedStyles[0].settings.default.textFontSize;
            currentStyle.textMaxResolution = computedStyles[0].settings.default.textMaxResolution;
            currentStyle.textStrokeColor = computedStyles[0].settings.default.textStrokeColor;
            currentStyle.textStrokeWidth = computedStyles[0].settings.default.textStrokeWidth;
        }
    }
    return getFeatureStyle(currentStyle, feature, resolution, zIndex, feedbackFields);
}

function getCustomStyle(displayFeatureProperties, customStylingOptions, hideFill = false) {
    // Check if key exists in displayFeatureProperties, regardless of the actual value
    if (customStylingOptions.attribute in displayFeatureProperties) {
        let attributeValue = displayFeatureProperties[customStylingOptions.attribute];
        // Treat null values the same as empty values
        if (attributeValue === null) {
            attributeValue = '';
        }

        let currentFeatureStyle;
        if (customStylingOptions.classificationType === STYLE_CLASSIFICATION_TYPES.RANGES) {
            // style feature based on numeric value that is between a range
            currentFeatureStyle = customStylingOptions.rules.find((rule) => attributeValue >= rule.lowerBound && attributeValue <= rule.upperBound);
        } else {
            // style feature based on string value or numeric value
            currentFeatureStyle = customStylingOptions.rules.find(({ filterLiteral }) => filterLiteral === attributeValue || Number(filterLiteral) === attributeValue);
        }

        const otherFeatureStyle = customStylingOptions.rules.find(({ filterLiteral }) => filterLiteral === CUSTOM_STYLING_RULE_OTHER);
        const featureStyle = currentFeatureStyle || (customStylingOptions.useOtherRule === true && otherFeatureStyle);

        // Make fill transparent when we need to hide it
        let fillColor = hideFill ? 'transparent' : featureStyle.fillColor;

        if (featureStyle) {
            return {
                fillColor: fillColor,
                pointSize: featureStyle.pointSize,
                strokeColor: featureStyle.strokeColor,
                strokeWidth: featureStyle.strokeWidth,
                icon: featureStyle.iconReplacedParams || featureStyle.icon,
                iconSize: featureStyle.iconSize,
                iconSizeUnits: featureStyle.iconSizeUnits,
                imageSvg: featureStyle.imageSvg,
                sizeSvg: featureStyle.sizeSvg,
                hideFill: hideFill
            };
        }
    }
    return {};
}

/**
 * Creates a cluster style
 * This shows an aggregated view of objects while zoomed out
 * This is used for Web-feedback layers
 * @return {OlStyle} the style of the Cluster layer
 */
export function getLayerClusterStyle(layerData, feature, resolution, publicEndPoint) {
    const { computedStyles } = layerData;
    const layerStyle = computedStyles[0].settings.default;
    const clusterStyle = { ...layerStyle };
    const featuresIncluster = feature.get('features');
    const mainFeatures = featuresIncluster
        .map(_feature => ({ feature: _feature, props: _feature.getProperties() }))
        .filter(({ props }) => props.aansluitnummer === null);
    const numberOfFeatures = mainFeatures.length;
    const showFeatureColors = true;
    const customStylingOptions = computedStyles[0].customStylingOptions;
    if (customStylingOptions && showFeatureColors && numberOfFeatures === 1) {
        const displayFeatureProperties = mainFeatures[0].props;
        if (displayFeatureProperties[customStylingOptions.attribute]) {
            const currentFeatureStyle = customStylingOptions.rules.find(({ filterLiteral }) => filterLiteral === displayFeatureProperties[customStylingOptions.attribute]);
            if (currentFeatureStyle) {
                clusterStyle.fillColor = currentFeatureStyle.fillColor;
                clusterStyle.pointSize = currentFeatureStyle.pointSize;
                clusterStyle.strokeColor = currentFeatureStyle.strokeColor;
                clusterStyle.strokeWidth = currentFeatureStyle.strokeWidth;
                clusterStyle.icon = currentFeatureStyle.iconReplacedParams || currentFeatureStyle.icon;
                clusterStyle.iconSize = currentFeatureStyle.iconSize;
                clusterStyle.iconSizeUnits = currentFeatureStyle.iconSizeUnits;
                clusterStyle.pointImage = null;
            }
        }
    }
    const clusterText = featuresIncluster.length;
    return getClusterStyle(feature, resolution, clusterStyle, clusterText, publicEndPoint);
}

async function setSvgImages(store, styleSettings) {
    const svgStylePromisses = styleSettings
        .map(styleSetting => {
            if (styleSetting.nameSvg && styleSetting.nameSvg !== '') {
                return store.dispatch('useApiData', {
                    name: 'PolygonSvgImage',
                    data: {
                        organizationId: store.getters.getOrganizationId,
                        svgName: styleSetting.nameSvg,
                        fill: styleSetting.fillSvg,
                        fillOpacity: styleSetting.fillOpacitySvg,
                        stroke: styleSetting.strokeSvg,
                        strokeOpacity: styleSetting.strokeOpacitySvg,
                        strokeWidth: styleSetting.strokeWidthSvg,
                    }
                });
            } else {
                return Promise.resolve();
            }
        });
    const svgStyleResults = await Promise.all(
        svgStylePromisses
    );
    styleSettings.forEach((setting, index) => {
        setting.imageSvg = svgStyleResults[index]?.data;
    });
}

export async function preloadSvgImages(store, styleSettings) {
    if (!styleSettings) {
        return;
    }
    await setSvgImages(store, styleSettings);
    for (const setting of styleSettings) {
        if (setting.customStylingOptions?.rules) {
            await setSvgImages(store, setting.customStylingOptions?.rules);
        }
    }
}

export async function getStyleSettings(styleSettings, rotationAttribute) {
    const settings = styleSettings ? [...styleSettings] : [];

    // create the object
    return settings.map(styleSetting => {
        const {
            customStylingOptions,
            id,
            maxResolution,
            maxScale,
            minResolution,
            minScale,
            textMaxResolution,
            name,
            pointImage
        } = styleSetting;
        return {
            customStylingOptions,
            id,
            maxResolution,
            maxScale,
            minResolution,
            minScale,
            textMaxResolution,
            name,
            settings: Object.entries(NEDGLOBE_VIEWER.styleDisplayTypes)
                .reduce((acc, [styleDisplayTypeName, styleDisplayType]) => {
                    acc[styleDisplayTypeName] = Object.values(NEDGLOBE_VIEWER.styleOptionNames)
                        .reduce((optionAcc, styleOptionName) => {
                            // Skip unsupported text styling options for selection/selected features
                            if (
                                (styleDisplayType === NEDGLOBE_VIEWER.styleDisplayTypes.selected ||
                                    styleDisplayType ===
                                    NEDGLOBE_VIEWER.styleDisplayTypes.selection) &&
                                styleOptionName.startsWith('text')
                            ) {
                                return optionAcc;
                            }
                            const settingName = `${styleOptionName}${styleDisplayType}`;

                            const isStrokeColorAttribute = styleDisplayType === NEDGLOBE_VIEWER.styleDisplayTypes.default &&
                                styleOptionName === NEDGLOBE_VIEWER.styleOptionNames.strokeColor;

                            const replacedParams = styleSetting?.[`${settingName}ReplacedParams`];
                            const isIconReplacedParams = styleOptionName === NEDGLOBE_VIEWER.styleOptionNames.icon && replacedParams;

                            const isSvgAttribute = styleDisplayType === NEDGLOBE_VIEWER.styleDisplayTypes.default &&
                                (styleOptionName === NEDGLOBE_VIEWER.styleOptionNames.imageSvg ||
                                    styleOptionName === NEDGLOBE_VIEWER.styleOptionNames.sizeSvg ||
                                    styleOptionName === NEDGLOBE_VIEWER.styleOptionNames.rotationSvg);

                            if (styleOptionName === NEDGLOBE_VIEWER.styleOptionNames.rotationAttribute && rotationAttribute) {
                                optionAcc[styleOptionName] = rotationAttribute;
                            } else if (styleOptionName === NEDGLOBE_VIEWER.styleOptionNames.textMaxResolution) {
                                optionAcc[styleOptionName] = styleSetting?.[styleOptionName];
                            } else if (isStrokeColorAttribute) {
                                optionAcc[styleOptionName] = styleSetting?.[settingName];
                                // Add stroke color and width for WFS with WMS when fill is hidden.
                                optionAcc['strokeColorNoFill'] = styleSetting?.[settingName];
                                optionAcc['strokeWidthNoFill'] = styleSetting?.['strokeWidthDefault'];
                            } else if (isSvgAttribute) {
                                optionAcc[styleOptionName] = styleSetting?.[styleOptionName];
                            } else if (isIconReplacedParams) {
                                optionAcc[styleOptionName] = replacedParams;
                            } else {
                                optionAcc[styleOptionName] = styleSetting?.[settingName];
                            }
                            return optionAcc;
                        }, { pointImage });
                    return acc;
                }, {})
        };
    });
}
